import {
    useAddFreshCollection,
    useCheckIfFreshCollection,
    useGetCompetitionCollections,
    useGetFantasyCollectionValidity,
} from '@pkg/hooks';
import { type SupportedChain } from '@pkg/types';
import { capitalizeFirstLetter } from '@pkg/utils';

import { CompetitionCollectionStatus } from '@app/client';
import { type FreshCollection, useFantasyCollectionValidityStore } from '@app/store/zustand';

export function useValidateFreshCollections() {
    const { collectionsStatuses, collectionsValidity } = useFantasyCollectionValidityStore();
    const checkForFreshCollections = useCheckIfFreshCollection();
    const checkCollectionValidity = useGetFantasyCollectionValidity();
    const getCollectionStatus = useGetCompetitionCollections();
    const addFreshCollections = useAddFreshCollection();
    const handleFreshCheck = (items: FreshCollection[], chain: SupportedChain) => {
        const slugs = items?.map((item) => item?.slug).filter((slug) => slug?.length);
        if (slugs && slugs?.length) {
            checkForFreshCollections(slugs as string[]).then(async (freshCollectionSlugs) => {
                const freshItems = items.filter(
                    (item) => item?.slug && freshCollectionSlugs?.includes(item?.slug),
                ) as FreshCollection[];
                if (freshItems?.length) {
                    await addFreshCollections(freshItems, chain);
                }
            });
        }
    };

    const handleStatusCheck = async (items: FreshCollection[], chain: SupportedChain) => {
        const collectionSlugs = items?.reduce(
            (acc, item) => {
                const slug = item?.slug || '';
                const storedStatus = collectionsStatuses[slug];
                if (!storedStatus) {
                    acc.uncheckedSlugs = [...acc.uncheckedSlugs, slug];
                } else if (storedStatus === CompetitionCollectionStatus.Normal) {
                    acc.normalCollections = [...acc.normalCollections, item];
                }
                return acc;
            },

            {
                uncheckedSlugs: [] as string[],
                normalCollections: [] as FreshCollection[],
            },
        );
        if (collectionSlugs.normalCollections?.length) {
            handleFreshCheck(collectionSlugs.normalCollections, chain);
        }
        getCollectionStatus(collectionSlugs.uncheckedSlugs).then(async (statuses) => {
            const normalCollections = items.filter((item) => {
                const notNormal = statuses?.find(
                    (statusItem) =>
                        statusItem.slug === item.slug &&
                        statusItem.status &&
                        statusItem.status !== CompetitionCollectionStatus.Normal,
                );

                return !notNormal;
            });
            if (normalCollections?.length) {
                handleFreshCheck(normalCollections, chain);
            }
        });
    };

    const checkChainValidity = async (collectionSlugs, chain, collectionItems) => {
        await checkCollectionValidity({
            ignoreSignal: true,
            collectionSlugs,
            updateStore: true,
            chain: capitalizeFirstLetter(chain),
        }).then(async (validityData) => {
            if (validityData) {
                const validityItems = Object.keys(validityData);
                const validItems = validityItems
                    ?.map((slug) => {
                        const itemIsValid = validityData[slug]?.is_valid;
                        if (itemIsValid) {
                            return collectionItems?.find((item) => item.slug === slug);
                        }
                    })
                    .filter((item) => item !== undefined) as FreshCollection[];
                if (validItems?.length) {
                    await handleStatusCheck(validItems, chain);
                }
            }
        });
    };

    return async (items: FreshCollection[]) => {
        const collections = items?.reduce(
            (acc, item) => {
                const itemSlug = item?.slug || '';
                const itemChain = item?.chain;
                const itemValidity = collectionsValidity[itemSlug];
                if (!itemValidity) {
                    switch (itemChain) {
                        // TODO neaten or move to util
                        case 'aptos': {
                            acc.unChecked.aptos.items = [...acc.unChecked.aptos.items, item];
                            acc.unChecked.aptos.slugs = [...acc.unChecked.aptos.slugs, itemSlug];
                            break;
                        }
                        case 'polygon': {
                            acc.unChecked.polygon.items = [...acc.unChecked.polygon.items, item];
                            acc.unChecked.polygon.slugs = [...acc.unChecked.polygon.slugs, itemSlug];
                            break;
                        }
                        case 'ethereum':
                        default: {
                            acc.unChecked.ethereum.items = [...acc.unChecked.ethereum.items, item];
                            acc.unChecked.ethereum.slugs = [...acc.unChecked.ethereum.slugs, itemSlug];
                        }
                    }
                } else if (itemValidity?.is_valid) {
                    switch (itemChain) {
                        case 'aptos': {
                            acc.valid.aptos.items = [...acc.valid.aptos.items, item];
                            acc.valid.aptos.slugs = [...acc.valid.aptos.slugs, itemSlug];
                            break;
                        }
                        case 'polygon': {
                            acc.valid.polygon.items = [...acc.valid.polygon.items, item];
                            acc.valid.polygon.slugs = [...acc.valid.polygon.slugs, itemSlug];
                            break;
                        }
                        case 'ethereum':
                        default: {
                            acc.valid.ethereum.items = [...acc.valid.ethereum.items, item];
                            acc.valid.ethereum.slugs = [...acc.valid.ethereum.slugs, itemSlug];
                        }
                    }
                }
                return acc;
            },
            {
                valid: {
                    ethereum: { slugs: [] as string[], items: [] as FreshCollection[] },
                    polygon: { slugs: [] as string[], items: [] as FreshCollection[] },
                    aptos: { slugs: [] as string[], items: [] as FreshCollection[] },
                },
                unChecked: {
                    ethereum: { slugs: [] as string[], items: [] as FreshCollection[] },
                    polygon: { slugs: [] as string[], items: [] as FreshCollection[] },
                    aptos: { slugs: [] as string[], items: [] as FreshCollection[] },
                },
            },
        );

        await Promise.all(
            Object.keys(collections.valid)?.map(
                (chain) =>
                    collections?.valid[chain].slugs?.length &&
                    handleStatusCheck(collections?.valid[chain].items, chain as SupportedChain),
            ),
        );

        await Promise.all(
            Object.keys(collections?.unChecked).map((chain) => {
                const { items, slugs } = collections.unChecked[chain];
                if (slugs?.length) {
                    checkChainValidity(slugs, chain, items);
                }
            }),
        );
    };
}
